import React, { useState, useEffect } from 'react'
import { Tab } from '@headlessui/react'
import {
  BanknotesIcon,
  UserGroupIcon,
  ChartBarIcon,
  CreditCardIcon,
  ClipboardDocumentListIcon,
  GiftIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { api, complianceAPI } from '../services/api'
import {
  CreatorEarnings,
  Revenue,
  SubscriptionTier,
  Subscription,
  Transaction,
  Payout,
  PayoutRequestResult,
} from '../types'
import RevenueAnalytics from '../components/monetization/RevenueAnalytics'
import Spinner from '../components/Spinner'
import Tooltip from '../components/Tooltip'
import { MONETIZATION_GLOSSARY } from '../config/glossary'
import PricingTierCard from '../components/monetization/PricingTierCard'
import CreateTierModal from '../components/monetization/CreateTierModal'
import SubscribersList from '../components/monetization/SubscribersList'
import TransactionHistory from '../components/monetization/TransactionHistory'
import PayoutHistory from '../components/monetization/PayoutHistory'
import DripSchedule from '../components/drip/DripSchedule'
import AudiencePanel from '../components/monetization/AudiencePanel'
import TipGoalsManager from '../components/monetization/TipGoalsManager'
import PollsManager from '../components/polls/PollsManager'
import ReferralSettingsCard from '../components/referrals/ReferralSettingsCard'
import GetPaidChecklist, { ConnectStatus } from '../components/monetization/GetPaidChecklist'
import { useFeeRates } from '../components/monetization/FeeEstimate'
import { useAuthStore } from '../store/authStore'
import { VettingApplicationModal } from '../components/monetization/VettingApplicationForm'
import type { CreatorVettingResponse } from '../types'

const Monetization: React.FC = () => {
  const [earnings, setEarnings] = useState<CreatorEarnings>({
    total_earnings: 0,
    pending_balance: 0,
    available_balance: 0,
    lifetime_earnings: 0,
    total_subscribers: 0,
    active_subscriptions: 0,
    monthly_recurring_revenue: 0,
    average_revenue_per_user: 0,
  })
  const [revenueHistory, setRevenueHistory] = useState<Revenue[]>([])
  const [tiers, setTiers] = useState<SubscriptionTier[]>([])
  const [subscribers, setSubscribers] = useState<Subscription[]>([])
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [payouts, setPayouts] = useState<Payout[]>([])
  const [isRequestingPayout, setIsRequestingPayout] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateTierModal, setShowCreateTierModal] = useState(false)
  const [editingTier, setEditingTier] = useState<SubscriptionTier | undefined>(undefined)
  const [connectStatus, setConnectStatus] = useState<ConnectStatus | null>(null)
  // undefined while loading, null if it couldn't be loaded (then the server's
  // 403 is the gate, as before).
  const [vetting, setVetting] = useState<CreatorVettingResponse | null | undefined>(undefined)
  const [showVettingModal, setShowVettingModal] = useState(false)
  const feeRates = useFeeRates()
  const user = useAuthStore((state) => state.user)
  const pageUrl = user ? `${window.location.origin}/creator/${user.id}` : null

  useEffect(() => {
    loadMonetizationData()
    loadConnectStatus()
    loadVetting()
  }, [])

  const loadVetting = async () => {
    try {
      const data = await complianceAPI.getMyVetting()
      setVetting(data && typeof data.status === 'string' ? data : null)
    } catch {
      setVetting(null)
    }
  }

  // Tiers can't be created until the creator is approved (the server returns
  // 403 otherwise), so say so up front instead of letting the form fail.
  const tierBlocked = !!vetting && vetting.status !== 'approved'
  const openTierForm = () => {
    setEditingTier(undefined)
    setShowCreateTierModal(true)
  }

  // Separate from the rest: a Stripe hiccup here shouldn't hide the earnings.
  const loadConnectStatus = async () => {
    try {
      const { data } = await api.get<ConnectStatus>('/api/monetization/connect/status')
      setConnectStatus(data && typeof data.status === 'string' ? data : null)
    } catch {
      setConnectStatus(null)
    }
  }

  const loadMonetizationData = async () => {
    setIsLoading(true)
    try {
      const [
        earningsRes,
        revenueRes,
        tiersRes,
        subscribersRes,
        transactionsRes,
        payoutsRes,
      ] = await Promise.all([
        api.get('/api/monetization/earnings'),
        api.get('/api/monetization/revenue'),
        api.get('/api/monetization/tiers'),
        api.get('/api/monetization/subscribers'),
        api.get('/api/monetization/transactions'),
        api.get('/api/monetization/payouts'),
      ])

      setEarnings(earningsRes.data)
      setRevenueHistory(revenueRes.data)
      setTiers(tiersRes.data)
      setSubscribers(subscribersRes.data)
      setTransactions(transactionsRes.data)
      setPayouts(payoutsRes.data)
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to load monetization data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateTier = async (tierData: Partial<SubscriptionTier>) => {
    try {
      let saved: SubscriptionTier
      if (editingTier) {
        saved = (await api.patch<SubscriptionTier>(`/api/monetization/tiers/${editingTier.id}`, tierData)).data
        toast.success('Tier updated successfully')
      } else {
        saved = (await api.post<SubscriptionTier>('/api/monetization/tiers', tierData)).data
        toast.success('Tier created successfully')
      }
      loadMonetizationData()
      // The modal stays open on the saved tier to show "Live on Stripe".
      return saved
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to save tier')
    }
  }

  const handleEditTier = (tier: SubscriptionTier) => {
    setEditingTier(tier)
    setShowCreateTierModal(true)
  }

  const handleRequestPayout = async () => {
    setIsRequestingPayout(true)
    try {
      const { data: result } = await api.post<PayoutRequestResult>(
        '/api/monetization/payouts/request'
      )
      if (result.status === 'onboarding_required' && result.onboarding_url) {
        toast('Complete Stripe Connect onboarding to enable payouts', { icon: 'ℹ️' })
        window.location.href = result.onboarding_url
      } else if (result.status === 'insufficient_balance') {
        toast.error(result.message)
      } else {
        toast.success(result.message || 'Payout requested successfully')
        loadMonetizationData()
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to request payout')
    } finally {
      setIsRequestingPayout(false)
    }
  }

  const tabs = [
    { name: 'Overview', icon: ChartBarIcon },
    { name: 'Tiers', icon: BanknotesIcon },
    { name: 'Tip goals', icon: GiftIcon },
    { name: 'Polls', icon: ChatBubbleLeftRightIcon },
    { name: 'Subscribers', icon: UserGroupIcon },
    { name: 'Transactions', icon: CreditCardIcon },
    { name: 'Payouts', icon: ClipboardDocumentListIcon },
  ]

  return (
    // palette-shared: Light maps this page's blues and greys onto the shared
    // palette (theme/light-theme.css); Dark is unchanged.
    <div className="palette-shared min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Creator Monetization</h1>
          <p className="text-gray-600 mt-2">
            Manage your subscriptions, earnings, and payouts
          </p>
        </div>

        {!isLoading && (
          <GetPaidChecklist
            vetting={vetting ?? null}
            onApply={() => setShowVettingModal(true)}
            connect={connectStatus}
            tierCount={tiers.length}
            liveTierCount={tiers.filter((t) => t.stripe_price_id_monthly).length}
            subscriberCount={subscribers.length}
            pageUrl={pageUrl}
            onCreateTier={openTierForm}
          />
        )}

        <Tab.Group>
          <Tab.List className="flex space-x-1 rounded-xl bg-white p-1 shadow mb-6">
            {tabs.map((tab) => (
              <Tab
                key={tab.name}
                className={({ selected }) =>
                  `w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-colors
                  ${
                    selected
                      ? 'bg-blue-600 text-white shadow'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`
                }
              >
                {({ selected }) => (
                  <div className="flex items-center justify-center gap-2">
                    <tab.icon className="h-5 w-5" />
                    <span className="hidden sm:inline">{tab.name}</span>
                  </div>
                )}
              </Tab>
            ))}
          </Tab.List>

          <Tab.Panels>
            <Tab.Panel>
              <RevenueAnalytics
                earnings={earnings}
                revenueHistory={revenueHistory}
                isLoading={isLoading}
              />
              <AudiencePanel />
            </Tab.Panel>

            <Tab.Panel>
              <div className="space-y-6">
                <div className="flex flex-wrap justify-between items-center gap-3">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                      Subscription Tiers
                      <Tooltip content={MONETIZATION_GLOSSARY.tiers} label="a subscription tier" />
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Create and manage your subscription offerings
                    </p>
                  </div>
                  <button
                    onClick={openTierForm}
                    disabled={tierBlocked}
                    aria-describedby={tierBlocked ? 'tier-vetting-gate' : undefined}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Create New Tier
                  </button>
                </div>

                {tierBlocked && vetting && (
                  <div
                    id="tier-vetting-gate"
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
                  >
                    <p className="text-sm text-amber-800">
                      {vetting.status === 'pending'
                        ? 'You can create tiers once your application to monetize is approved. It’s under review now.'
                        : vetting.status === 'rejected'
                        ? 'Your application to monetize wasn’t approved, so tiers are locked. Resubmit it to try again.'
                        : 'Creating tiers opens once you’re approved to monetize. It’s a short application.'}
                    </p>
                    {vetting.status !== 'pending' && (
                      <button
                        type="button"
                        onClick={() => setShowVettingModal(true)}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                      >
                        {vetting.status === 'rejected' ? 'Resubmit application' : 'Apply to monetize'}
                      </button>
                    )}
                  </div>
                )}

                {isLoading ? (
                  <div className="flex justify-center items-center py-12">
                    <Spinner size="lg" />
                  </div>
                ) : tiers.length === 0 ? (
                  <div className="text-center py-12 px-4 bg-white rounded-lg shadow">
                    <BanknotesIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-900 font-medium mb-2">Create your first plan</p>
                    <p className="text-sm text-gray-500 mb-4">
                      A plan is what fans pay for each month. For example:
                    </p>
                    <div className="max-w-xs mx-auto text-left border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
                      <p className="font-semibold text-gray-900">
                        Pro <span className="font-normal text-gray-600">— $15/month</span>
                      </p>
                      <ul className="mt-2 space-y-1 text-sm text-gray-700 list-disc list-inside">
                        <li>Every call, the moment it's locked</li>
                        <li>Each call&rsquo;s receipt, live, not after the delay</li>
                        <li>Your full track record, losses included</li>
                      </ul>
                    </div>
                    <button
                      onClick={openTierForm}
                      disabled={tierBlocked}
                      aria-describedby={tierBlocked ? 'tier-vetting-gate' : undefined}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Create a plan
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {tiers.map((tier) => (
                      <PricingTierCard
                        key={tier.id}
                        tier={tier}
                        onEdit={handleEditTier}
                        isCreator={true}
                      />
                    ))}
                  </div>
                )}
                {!isLoading && <DripSchedule tiers={tiers} />}
              </div>
            </Tab.Panel>

            <Tab.Panel>
              {user && (
                <TipGoalsManager
                  creatorId={user.id}
                  blockedMessage={
                    !tierBlocked || !vetting
                      ? null
                      : vetting.status === 'pending'
                      ? 'You can create tip goals once your application to monetize is approved. It’s under review now.'
                      : 'Tip goals open once you’re approved to monetize. Apply from the Tiers tab.'
                  }
                />
              )}
            </Tab.Panel>

            <Tab.Panel>
              {user && (
                <PollsManager
                  creatorId={user.id}
                  blockedMessage={
                    !tierBlocked || !vetting
                      ? null
                      : vetting.status === 'pending'
                      ? 'You can post polls once your application to monetize is approved. It’s under review now.'
                      : 'Polls open once you’re approved to monetize. Apply from the Tiers tab.'
                  }
                />
              )}
            </Tab.Panel>

            <Tab.Panel>
              <ReferralSettingsCard />
              <SubscribersList subscribers={subscribers} isLoading={isLoading} pageUrl={pageUrl} />
            </Tab.Panel>

            <Tab.Panel>
              <TransactionHistory transactions={transactions} currentUserId={user?.id} isLoading={isLoading} />
            </Tab.Panel>

            <Tab.Panel>
              <PayoutHistory
                payouts={payouts}
                onRequestPayout={handleRequestPayout}
                isLoading={isLoading}
                isRequesting={isRequestingPayout}
                feeRates={feeRates}
              />
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>

        <VettingApplicationModal
          isOpen={showVettingModal}
          resubmit={vetting?.status === 'rejected'}
          onClose={() => setShowVettingModal(false)}
          onSubmitted={(result) => {
            setVetting(result)
            setShowVettingModal(false)
          }}
        />

        <CreateTierModal
          isOpen={showCreateTierModal}
          onClose={() => {
            setShowCreateTierModal(false)
            setEditingTier(undefined)
          }}
          onSave={handleCreateTier}
          tier={editingTier}
          feeRates={feeRates}
        />
      </div>
    </div>
  )
}

export default Monetization
